import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { calendarService, queryKeys } from "@/services";
import type {
  CalendarEventManage,
  CreateCalendarEventDto,
  CreateCalendarEventSeriesDto,
  RecurrenceScope,
  UpdateCalendarEventDto,
} from "@/types";
import { getMonthKeyFromIso } from "@/utils/calendarMonthRange";

interface UpdateCalendarEventVariables {
  id: string;
  body: UpdateCalendarEventDto;
  previousStartsAt?: string;
  scope?: RecurrenceScope;
}

interface RemoveCalendarEventVariables {
  id: string;
  scope?: RecurrenceScope;
}

const invalidateCalendarMineMonths = (
  queryClient: QueryClient,
  tenantSlug: string,
  monthKeys: Iterable<string>,
) => {
  const months = new Set(monthKeys);

  return queryClient.invalidateQueries({
    queryKey: queryKeys.calendar.mineAll(tenantSlug),
    predicate: (query) => {
      const monthKey = query.queryKey[4];
      return typeof monthKey === "string" && months.has(monthKey);
    },
  });
};

/**
 * Calendar event mutations with per-month cache invalidation on success
 */
export const useCalendarEventMutations = (tenantSlug: string) => {
  const queryClient = useQueryClient();

  const invalidateMonths = (monthKeys: Iterable<string>) =>
    invalidateCalendarMineMonths(queryClient, tenantSlug, monthKeys);

  const create = useMutation({
    mutationFn: (body: CreateCalendarEventDto) => calendarService.create(body),
    onSuccess: (event) => invalidateMonths([getMonthKeyFromIso(event.startsAt)]),
  });

  const update = useMutation({
    mutationFn: ({ id, body, scope }: UpdateCalendarEventVariables) =>
      scope && scope !== 'THIS'
        ? calendarService.updateWithScope(id, body, scope)
        : calendarService.update(id, body),
    onSuccess: (result, variables) => {
      const events = Array.isArray(result) ? result : [result];
      const monthKeys = new Set(events.map((e) => getMonthKeyFromIso(e.startsAt)));

      if (variables.previousStartsAt) {
        monthKeys.add(getMonthKeyFromIso(variables.previousStartsAt));
      }

      void invalidateMonths(monthKeys);
    },
  });

  const remove = useMutation({
    mutationFn: ({ id, scope }: RemoveCalendarEventVariables) =>
      calendarService.remove(id, scope),
    onSuccess: (result) => {
      const events = Array.isArray(result) ? result : [result];
      void invalidateMonths(events.map((e) => getMonthKeyFromIso(e.startsAt)));
    },
  });

  const createSeries = useMutation({
    mutationFn: (body: CreateCalendarEventSeriesDto) => calendarService.createSeries(body),
    onSuccess: (events: CalendarEventManage[]) => {
      const monthKeys = new Set(events.map((e) => getMonthKeyFromIso(e.startsAt)));
      void invalidateMonths(monthKeys);
    },
  });

  return { create, update, remove, createSeries };
};
